import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import adminClient from '@/api/adminClient';
import type { RecognitionCandidate, CandidateStatus, CandidateSource, Room } from '@/types';

const STATUS_COLORS: Record<CandidateStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  reviewed: 'bg-gray-100 text-gray-800',
  added: 'bg-green-100 text-green-800',
  dismissed: 'bg-red-100 text-red-800',
};

const SOURCE_LABELS: Record<CandidateSource, string> = {
  auto: '자동 감지',
  admin: '관리자',
  mobile: '모바일 사용자',
};

const REASON_LABELS: Record<string, string> = {
  low_confidence: '낮은 신뢰도',
  repeated_pattern: '반복 패턴',
  abnormal_chars: '비정상 문자',
  length_mismatch: '길이 불일치',
  user_flagged: '사용자 플래그',
};

export default function CandidatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string>(searchParams.get('roomId') || '1');
  const [candidates, setCandidates] = useState<RecognitionCandidate[]>([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, reviewed: 0, added: 0, dismissed: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<CandidateStatus | ''>('pending');
  const [sourceFilter, setSourceFilter] = useState<CandidateSource | ''>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchOptions, setBatchOptions] = useState({ addOriginal: true, addSuggested: false });
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [reviewComment, setReviewComment] = useState('');

  // Load rooms
  useEffect(() => {
    const loadRooms = async () => {
      try {
        const roomList = await adminClient.getRooms();
        setRooms(roomList);
      } catch (error) {
        console.error('Failed to load rooms:', error);
      }
    };
    loadRooms();
  }, []);

  // Load candidates
  useEffect(() => {
    const loadCandidates = async () => {
      setLoading(true);
      try {
        const result = await adminClient.getCandidates(selectedRoomId, {
          status: statusFilter || undefined,
          source: sourceFilter || undefined,
          limit: 100,
        });
        setCandidates(result.candidates);
        setStats({
          total: result.total,
          pending: result.pending,
          reviewed: result.reviewed,
          added: result.added,
          dismissed: result.dismissed,
        });
      } catch (error) {
        console.error('Failed to load candidates:', error);
        setCandidates([]);
      } finally {
        setLoading(false);
      }
    };
    loadCandidates();
  }, [selectedRoomId, statusFilter, sourceFilter]);

  // Update URL params
  useEffect(() => {
    setSearchParams({ roomId: selectedRoomId });
  }, [selectedRoomId, setSearchParams]);

  const handleSelectAll = () => {
    if (selectedIds.size === candidates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(candidates.map(c => c.id)));
    }
  };

  const handleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const handleReview = async (candidateId: string, status: 'reviewed' | 'added' | 'dismissed') => {
    try {
      await adminClient.reviewCandidate(selectedRoomId, candidateId, {
        status,
        suggestedText: editText || undefined,
        reviewComment: reviewComment || undefined,
      });
      // Reload candidates
      const result = await adminClient.getCandidates(selectedRoomId, {
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        limit: 100,
      });
      setCandidates(result.candidates);
      setStats({
        total: result.total,
        pending: result.pending,
        reviewed: result.reviewed,
        added: result.added,
        dismissed: result.dismissed,
      });
      setReviewingId(null);
      setEditText('');
      setReviewComment('');
    } catch (error) {
      console.error('Failed to review candidate:', error);
      alert('검토 실패');
    }
  };

  const handleBatchAdd = async () => {
    if (selectedIds.size === 0) return;
    try {
      const result = await adminClient.batchAddToPhraseList(
        selectedRoomId,
        Array.from(selectedIds),
        batchOptions
      );
      alert(`${result.addedPhrases.length}개 용어가 추가되었습니다.`);
      setShowBatchModal(false);
      setSelectedIds(new Set());
      // Reload
      const candidatesResult = await adminClient.getCandidates(selectedRoomId, {
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        limit: 100,
      });
      setCandidates(candidatesResult.candidates);
      setStats({
        total: candidatesResult.total,
        pending: candidatesResult.pending,
        reviewed: candidatesResult.reviewed,
        added: candidatesResult.added,
        dismissed: candidatesResult.dismissed,
      });
    } catch (error) {
      console.error('Failed to batch add:', error);
      alert('일괄 추가 실패');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">인식 품질 모니터링</h1>
        <div className="flex items-center space-x-4">
          <select
            value={selectedRoomId}
            onChange={(e) => setSelectedRoomId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            {rooms.map((room) => (
              <option key={room.id} value={room.id}>
                {room.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">전체</div>
          <div className="text-2xl font-bold">{stats.total}</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg shadow">
          <div className="text-sm text-yellow-700">대기중</div>
          <div className="text-2xl font-bold text-yellow-800">{stats.pending}</div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">검토됨</div>
          <div className="text-2xl font-bold text-gray-700">{stats.reviewed}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg shadow">
          <div className="text-sm text-green-700">추가됨</div>
          <div className="text-2xl font-bold text-green-800">{stats.added}</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg shadow">
          <div className="text-sm text-red-700">무시됨</div>
          <div className="text-2xl font-bold text-red-800">{stats.dismissed}</div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow">
        <div className="flex items-center space-x-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CandidateStatus | '')}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">모든 상태</option>
            <option value="pending">대기중</option>
            <option value="reviewed">검토됨</option>
            <option value="added">추가됨</option>
            <option value="dismissed">무시됨</option>
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as CandidateSource | '')}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">모든 소스</option>
            <option value="auto">자동 감지</option>
            <option value="admin">관리자</option>
            <option value="mobile">모바일</option>
          </select>
        </div>
        <div className="flex items-center space-x-2">
          {selectedIds.size > 0 && (
            <>
              <span className="text-sm text-gray-500">{selectedIds.size}개 선택됨</span>
              <button
                onClick={() => setShowBatchModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                일괄 용어집 추가
              </button>
            </>
          )}
        </div>
      </div>

      {/* Candidates Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">로딩 중...</div>
        ) : candidates.length === 0 ? (
          <div className="p-8 text-center text-gray-500">검토할 후보가 없습니다.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === candidates.length && candidates.length > 0}
                    onChange={handleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">소스</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">원본 텍스트</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">신뢰도</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">감지 이유</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">시간</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">작업</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {candidates.map((candidate) => (
                <tr key={candidate.id} className={reviewingId === candidate.id ? 'bg-blue-50' : ''}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(candidate.id)}
                      onChange={() => handleSelect(candidate.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[candidate.status]}`}>
                      {candidate.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {SOURCE_LABELS[candidate.source]}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{candidate.originalText}</div>
                    {candidate.suggestedText && (
                      <div className="text-xs text-green-600">→ {candidate.suggestedText}</div>
                    )}
                    {candidate.context?.before && (
                      <div className="text-xs text-gray-400 mt-1">
                        문맥: ...{candidate.context.before.slice(-30)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {candidate.confidence > 0 ? (
                      <span className={candidate.confidence < 0.7 ? 'text-red-600' : 'text-gray-600'}>
                        {(candidate.confidence * 100).toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {candidate.detectionReason ? REASON_LABELS[candidate.detectionReason] : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(candidate.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    {candidate.status === 'pending' ? (
                      reviewingId === candidate.id ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            placeholder="수정 텍스트 (선택)"
                            className="w-full px-2 py-1 text-sm border rounded"
                          />
                          <input
                            type="text"
                            value={reviewComment}
                            onChange={(e) => setReviewComment(e.target.value)}
                            placeholder="코멘트 (선택)"
                            className="w-full px-2 py-1 text-sm border rounded"
                          />
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleReview(candidate.id, 'added')}
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              추가
                            </button>
                            <button
                              onClick={() => handleReview(candidate.id, 'dismissed')}
                              className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              무시
                            </button>
                            <button
                              onClick={() => {
                                setReviewingId(null);
                                setEditText('');
                                setReviewComment('');
                              }}
                              className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500"
                            >
                              취소
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setReviewingId(candidate.id);
                            setEditText(candidate.suggestedText || '');
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          검토
                        </button>
                      )
                    ) : (
                      <span className="text-xs text-gray-400">
                        {candidate.reviewedBy && `검토: ${candidate.reviewedBy}`}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Batch Add Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 space-y-4">
            <h2 className="text-lg font-bold">일괄 용어집 추가</h2>
            <p className="text-sm text-gray-600">
              선택된 {selectedIds.size}개 후보를 용어집에 추가합니다.
            </p>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={batchOptions.addOriginal}
                  onChange={(e) => setBatchOptions({ ...batchOptions, addOriginal: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">원본 텍스트 추가</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={batchOptions.addSuggested}
                  onChange={(e) => setBatchOptions({ ...batchOptions, addSuggested: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">제안 텍스트 추가 (있는 경우)</span>
              </label>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowBatchModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                취소
              </button>
              <button
                onClick={handleBatchAdd}
                disabled={!batchOptions.addOriginal && !batchOptions.addSuggested}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                추가
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
